import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";

const useCountUp = (target, durationMs = 1400, decimals = 0) => {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const start = performance.now();
            const animate = (now) => {
              const t = Math.min(1, (now - start) / durationMs);
              const eased = 1 - Math.pow(1 - t, 3);
              setVal(eased * target);
              if (t < 1) requestAnimationFrame(animate);
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [target, durationMs]);

  return [ref, decimals ? val.toFixed(decimals) : Math.round(val)];
};

const StatBlock = ({ value, suffix, label, accent }) => {
  const [ref, n] = useCountUp(value);
  return (
    <div
      ref={ref}
      className="px-4 py-6 sm:px-6 sm:py-8 text-center sm:text-left"
    >
      <p
        className={`text-4xl sm:text-5xl font-black tracking-tight ${accent}`}
        style={{ fontFamily: "Outfit, sans-serif" }}
      >
        {n}
        {suffix}
      </p>
      <p className="mt-2 text-xs sm:text-sm tracking-wider uppercase text-slate-500 font-semibold">
        {label}
      </p>
    </div>
  );
};

const Stats = () => {
  const { t } = useLanguage();
  return (
    <section
      data-testid="stats-section"
      className="relative -mt-4 pb-4"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="rounded-3xl bg-blue-950 text-white relative overflow-hidden">
          {/* Decorative grain */}
          <div className="grain"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-950 to-slate-900 opacity-90" />
          <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full bg-red-700/10 blur-3xl" />
          <div className="relative grid grid-cols-2 sm:grid-cols-4 divide-x divide-white/10">
            <StatBlock
              value={16000}
              suffix="+"
              label={t.stats.mcq}
              accent="text-amber-400"
            />
            <StatBlock
              value={8}
              suffix=""
              label={t.stats.subjects}
              accent="text-white"
            />
            <StatBlock
              value={2}
              suffix=""
              label={t.stats.languages}
              accent="text-emerald-300"
            />
            <div className="px-4 py-6 sm:px-6 sm:py-8 text-center sm:text-left">
              <p
                className="text-4xl sm:text-5xl font-black tracking-tight text-amber-400"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                4.8
                <span className="text-white text-2xl align-top ml-0.5">★</span>
              </p>
              <p className="mt-2 text-xs sm:text-sm tracking-wider uppercase text-slate-300 font-semibold">
                {t.stats.rating}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats;
